import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchSymbolDay, type PredictionRow } from "../api/client";
import { CandleChart, type PriceMarker } from "../lib/CandleChart";
import { modelColor } from "../lib/modelColor";
import { signalFor, signalColor as signalColorOf } from "../lib/decisions";
import { isBackfilledModel } from "../lib/decisions";
import { buildIndicatorLinesFromBars } from "../lib/indicatorChart";

/**
 * "What did the models do today" view. One full-day candle chart with a
 * colored dot per (decision_ts, model_id) so a stray model jumps out
 * visually, plus a log table below for detail.
 *
 * `date` is the viewer's local calendar day (passed in from SymbolPage as a
 * date string). Backend treats it as a string prefix on decision_ts, so the
 * day boundary matches whatever the browser's local TZ thinks "today" is.
 */

function fmtPct(v: number | undefined | null) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(2)}%`;
}
function fmtNum(v: number | undefined | null, digits = 4) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return Number(v).toFixed(digits);
}
function fmtTime(iso: string) {
  // Display in browser's local TZ so the rendered times line up with the
  // chart's local-TZ x-axis labels from lightweight-charts.
  try {
    const d = new Date(iso.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + "Z");
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export function SymbolDayView({ symbol }: { symbol: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  // Default to today's local-calendar date.
  const todayLocal = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  // Honor ?date=YYYY-MM-DD so deep-links from the ModelDetailPage's date
  // headers land on the right day. Falls back to today if absent or malformed.
  const initialDate = useMemo(() => {
    const q = searchParams.get("date");
    return q && /^\d{4}-\d{2}-\d{2}$/.test(q) ? q : todayLocal;
  }, [searchParams, todayLocal]);
  const [date, setDateState] = useState(initialDate);
  const setDate = (next: string) => {
    setDateState(next);
    // Mirror the picker's value back to the URL so back-button + bookmarking work.
    const params = new URLSearchParams(searchParams);
    if (next && next !== todayLocal) params.set("date", next);
    else params.delete("date");
    setSearchParams(params, { replace: true });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["symbol-day", symbol, date],
    queryFn: () => fetchSymbolDay(symbol, date),
    enabled: !!symbol && !!date,
  });

  // Build one marker per (decision_ts, model_id). Color = hash(model_id) so a
  // stray model's dot lands at a different hue than its peers in the cluster.
  // size: 3 to make them clearly visible against the candles (lightweight-
  // charts' default size 1 reads as a tiny speck on a busy chart).
  const markers: PriceMarker[] = useMemo(() => {
    const rows: PredictionRow[] = data?.predictions ?? [];
    return rows
      .filter((r) => r.current_price !== undefined && r.current_price !== null)
      .map((r) => ({
        time: r.decision_ts,
        price: Number(r.current_price),
        color: modelColor(String(r.model_id)),
        text: undefined,
        shape: "circle" as const,
        position: "aboveBar" as const,
        size: 3,
      }));
  }, [data]);

  // Same indicators as the replay page (returns, vol, atr, vwap, time-of-day),
  // routed across panes so the smaller-magnitude lines aren't flattened.
  const indicatorLines = useMemo(
    () => buildIndicatorLinesFromBars(data?.bars),
    [data?.bars],
  );

  // Build a per-model legend (color swatch + truncated id) so the chart's
  // anonymous dots can be matched back to a model.
  const modelLegend = useMemo(() => {
    const ids = new Set<string>();
    for (const r of data?.predictions ?? []) ids.add(String(r.model_id));
    return Array.from(ids).sort();
  }, [data]);

  // Log table: every prediction row for the day, sorted by time then model.
  const logRows = useMemo(() => {
    const rows: PredictionRow[] = data?.predictions ?? [];
    return [...rows].sort((a, b) => {
      const t = a.decision_ts.localeCompare(b.decision_ts);
      if (t !== 0) return t;
      return String(a.model_id).localeCompare(String(b.model_id));
    });
  }, [data]);

  // Reset to today only when symbol changes AND there's no ?date= override
  // in the URL. Otherwise honor the deep-linked date.
  useEffect(() => {
    if (!searchParams.get("date")) setDate(todayLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  return (
    <div>
      <div className="toolbar">
        <label style={{ color: "#94a3b8", fontSize: 12 }}>Date</label>
        <input
          type="date"
          value={date}
          max={todayLocal}
          onChange={(e) => setDate(e.target.value)}
          style={{ padding: "6px 10px", background: "#0b1220", border: "1px solid #334155", borderRadius: 4, color: "#e2e8f0" }}
        />
        <span style={{ color: "#94a3b8", fontSize: 13 }}>
          {isLoading ? "loading…" : data ? `${data.predictions.length} prediction rows · ${data.bars.length} bars` : ""}
        </span>
      </div>

      {error && <div className="card" style={{ color: "#fca5a5" }}>{String(error)}</div>}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>{symbol} · {date}</h3>
        {data && data.bars.length > 0 ? (
          <CandleChart
            bars={data.bars}
            indicators={indicatorLines}
            markers={markers}
            height={640}
          />
        ) : (
          <div style={{ color: "#94a3b8" }}>
            {isLoading ? "Loading bars…" : "No bars for this day."}
          </div>
        )}
        {modelLegend.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12, fontSize: 11 }}>
            {modelLegend.map((id) => (
              <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: modelColor(id) }} />
                <code style={{ color: "#cbd5e1" }}>{id.length > 28 ? `${id.slice(0, 28)}…` : id}</code>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Decision log ({logRows.length})</h3>
        {logRows.length === 0 ? (
          <div style={{ color: "#94a3b8" }}>No prediction rows recorded for this day.</div>
        ) : (
          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Model</th>
                <th>Signal</th>
                <th className="num">Individual action</th>
                <th className="num">Ensemble</th>
                <th className="num">Current</th>
                <th className="num">Next</th>
                <th className="num">P&L</th>
              </tr>
            </thead>
            <tbody>
              {logRows.map((r) => {
                const sig = signalFor(r.signal_type, r.ensemble_action ?? r.individual_action);
                const c = modelColor(String(r.model_id));
                return (
                  <tr key={`${r.decision_ts}-${r.model_id}`}>
                    <td style={{ fontSize: 12 }}>
                      <Link to={`/replay/${encodeURIComponent(symbol)}/${encodeURIComponent(r.decision_ts)}`}>
                        {fmtTime(r.decision_ts)}
                      </Link>
                    </td>
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: c }} />
                        <code style={{ fontSize: 11 }}>
                          {String(r.model_id).length > 24 ? `${String(r.model_id).slice(0, 24)}…` : r.model_id}
                        </code>
                        {isBackfilledModel(r.model_id) && (
                          <span style={{ fontSize: 9, padding: "0 4px", borderRadius: 2, background: "#1f2937", color: "#fbbf24" }}>BACKFILL</span>
                        )}
                      </span>
                    </td>
                    <td style={{ color: signalColorOf(sig), fontWeight: 600 }}>{sig}</td>
                    <td className="num">{fmtNum(r.individual_action)}</td>
                    <td className="num">{fmtNum(r.ensemble_action)}</td>
                    <td className="num">{r.current_price !== undefined ? Number(r.current_price).toFixed(2) : "—"}</td>
                    <td className="num">{r.next_close !== undefined ? Number(r.next_close).toFixed(2) : "—"}</td>
                    <td className={`num ${r.pnl_pct === undefined ? "" : Number(r.pnl_pct) > 0 ? "pos" : "neg"}`}>
                      {fmtPct(r.pnl_pct)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </div>
  );
}
